// ═══════════════════════════════════════════════════════════════════════════════
// client/src/components/moonbeams/ShareableLinkRow.js
// ═══════════════════════════════════════════════════════════════════════════════
//
// One row in the shareable links section of a card on MyMoonbeams.
// Shows the link's label, view count, reaction summary, copy button,
// and a kebab menu for less-common actions (edit label, deactivate,
// reactivate).
//
// USAGE
//   <ShareableLinkRow
//     link={link}                        // shareable link object from server
//     onUpdated={(updatedLink) => ...}   // called after a successful mutation
//   />
//
// The parent (MyMoonbeams) owns the link list. When this row mutates the
// link (renames, deactivates, reactivates) it calls onUpdated with the
// fresh server-returned object so the parent can update its local cache.
//
// LAYOUT
//   • Active link:
//       "All grandkids" · 34 views · ❤️ 12  [Copy] [⋯]
//   • Deactivated link:
//       "Family text" · 0 views · (Disabled)  [Reactivate] [⋯]
//
// SAFETY
//   • Edit and deactivate buttons are guarded by inline confirmation —
//     no destructive operation happens without an explicit second click.
//   • The kebab menu closes on click outside.
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useRef } from 'react';
import moonbeamService from '../../services/moonbeamService.js';

export default function ShareableLinkRow({ link, onUpdated }) {
  // Local view of the link record. Initialized from props, updated in
  // place when the user edits or toggles. Keeps the row responsive
  // without waiting for the parent to re-render.
  const [current, setCurrent] = useState(link);
  useEffect(() => { setCurrent(link); }, [link]);

  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [labelDraft, setLabelDraft] = useState(current.label || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [copyFeedback, setCopyFeedback] = useState(null);

  const menuRef = useRef(null);
  const editInputRef = useRef(null);

  // ─── Close menu on outside click ─────────────────────────────────────
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [menuOpen]);

  // ─── Focus the edit input when entering edit mode ────────────────────
  useEffect(() => {
    if (editing && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editing]);

  // ─── Copy URL ─────────────────────────────────────────────────────────
  const handleCopy = async () => {
    setError(null);
    const url = current.shareUrl;
    if (!url) {
      setError('No URL available');
      return;
    }
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setCopyFeedback('Copied!');
      } else {
        // Legacy fallback for non-secure contexts
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (ok) setCopyFeedback('Copied!');
        else setCopyFeedback('Copy failed');
      }
    } catch {
      setCopyFeedback('Copy failed');
    }
    setTimeout(() => setCopyFeedback(null), 1800);
  };

  // ─── Toggle activate / deactivate ─────────────────────────────────────
  const handleToggleActive = async () => {
    setMenuOpen(false);
    if (busy) return;
    const targetState = !current.isactive;

    // Light confirmation when deactivating — reactivating doesn't warn
    // because it's recoverable and rarely surprising.
    if (!targetState) {
      const ok = window.confirm(
        'Deactivate this shareable link? People who already have the URL won\'t be able to view the card until you reactivate it.'
      );
      if (!ok) return;
    }

    setBusy(true);
    setError(null);
    try {
      const resp = await moonbeamService.updateShareableLink(current.linkid, { isactive: targetState });
      const updated = resp?.link || { ...current, isactive: targetState };
      setCurrent(prev => ({ ...prev, ...updated }));
      if (onUpdated) onUpdated(updated);
    } catch (err) {
      setError(err?.data?.error || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  // ─── Start editing the label ──────────────────────────────────────────
  const handleStartEdit = () => {
    setMenuOpen(false);
    setLabelDraft(current.label || '');
    setEditing(true);
    setError(null);
  };

  // ─── Save the new label ───────────────────────────────────────────────
  const handleSaveLabel = async () => {
    const trimmed = (labelDraft || '').trim();
    if (!trimmed) {
      setError('Label cannot be empty');
      return;
    }
    if (trimmed === current.label) {
      setEditing(false);
      return;
    }
    if (busy) return;

    setBusy(true);
    setError(null);
    try {
      const resp = await moonbeamService.updateShareableLink(current.linkid, { label: trimmed });
      const updated = resp?.link || { ...current, label: trimmed };
      setCurrent(prev => ({ ...prev, ...updated }));
      setEditing(false);
      if (onUpdated) onUpdated(updated);
    } catch (err) {
      setError(err?.data?.error || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setLabelDraft(current.label || '');
    setError(null);
  };

  // ─── Stats display ────────────────────────────────────────────────────
  const viewCount = parseInt(current.viewcount) || 0;
  const reactionCount = parseInt(current.reaction_count) || 0;
  const isActive = current.isactive !== false;

  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
      isActive
        ? 'border-royal-700/40 bg-royal-800/30'
        : 'border-royal-700/30 bg-royal-900/40 opacity-75'
    }`}>
      {/* ─── Label + Stats ──────────────────────────────────────── */}
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="flex items-center gap-2">
            <input
              ref={editInputRef}
              type="text"
              value={labelDraft}
              onChange={(e) => setLabelDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); handleSaveLabel(); }
                else if (e.key === 'Escape') { e.preventDefault(); handleCancelEdit(); }
              }}
              maxLength={255}
              disabled={busy}
              className="flex-1 px-2 py-1 bg-royal-900/60 border border-gold/40 rounded text-white text-sm focus:outline-none focus:border-gold"
            />
            <button
              onClick={handleSaveLabel}
              disabled={busy}
              className="text-sm text-gold hover:text-gold-light disabled:opacity-50 transition-colors"
            >
              Save
            </button>
            <button
              onClick={handleCancelEdit}
              disabled={busy}
              className="text-sm text-royal-100 hover:text-white disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex items-baseline gap-1.5 flex-wrap">
            <span className="text-white text-sm font-medium truncate">
              "{current.label}"
            </span>
            <span className="text-royal-100 text-sm">
              · {viewCount} view{viewCount !== 1 ? 's' : ''}
              {reactionCount > 0 && (
                <> · 💬 {reactionCount}</>
              )}
              {!isActive && (
                <span className="ml-1.5 text-amber-400">· Disabled</span>
              )}
            </span>
          </div>
        )}

        {error && (
          <p className="text-red-400 text-xs mt-1">{error}</p>
        )}
      </div>

      {/* ─── Actions ───────────────────────────────────────────── */}
      {!editing && (
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Copy button — only useful for active links */}
          {isActive && (
            <button
              onClick={handleCopy}
              disabled={busy}
              className="text-sm px-2.5 py-1 rounded-md border border-royal-600 text-royal-100 hover:text-gold hover:border-gold/50 transition-colors disabled:opacity-50"
              title="Copy share URL to clipboard"
            >
              {copyFeedback || 'Copy'}
            </button>
          )}

          {/* Kebab menu (⋯) */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              disabled={busy}
              aria-label="More actions"
              className="px-2 py-1 rounded-md text-royal-100 hover:text-white hover:bg-royal-800/60 transition-colors disabled:opacity-50"
            >
              ⋯
            </button>

            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 z-30 min-w-[180px] bg-royal-900 border border-royal-600 rounded-lg shadow-xl overflow-hidden">
                <button
                  onClick={handleStartEdit}
                  className="w-full text-left px-3 py-2 text-sm text-white hover:bg-royal-800 transition-colors"
                >
                  ✏️ Edit label
                </button>
                <button
                  onClick={handleToggleActive}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-royal-800 transition-colors ${
                    isActive ? 'text-amber-400' : 'text-green-400'
                  }`}
                >
                  {isActive ? '🚫 Deactivate' : '✓ Reactivate'}
                </button>
                {/* "View card" — opens the share URL in a new tab so the
                    sender can verify what recipients are seeing. Active
                    AND deactivated links both surface this for diagnostic
                    purposes; an inactive link will show the "no longer
                    available" page, which is itself useful info. */}
                {current.shareUrl && (
                  <a
                    href={current.shareUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMenuOpen(false)}
                    className="w-full block text-left px-3 py-2 text-sm text-white hover:bg-royal-800 transition-colors"
                  >
                    👁️ View card
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}